import { SignIn, SignUp } from '@clerk/clerk-react';
import { useState, useEffect } from 'react';
import {
  ArrowLeft,
  Heart,
  Shield,
  Users,
  Award,
  CheckCircle,
  Briefcase,
  Wheat,
  Building2,
  Truck,
  BarChart3,
  FileCheck,
  UserCheck,
  Activity,
} from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { ThemeToggle } from '../ThemeToggle';
import petsoLogo from '../../assets/4c0bdef843fbe05c3bb4b14b38ec794cce2e65c9.png';

export type UserType = 'petso' | 'petso-pro' | 'farmso';

interface ClerkLoginPageProps {
  userType: UserType;
  onBack?: () => void;
  redirectUrl?: string;
}

interface FeatureItem {
  icon: typeof Shield;
  title: string;
  description: string;
  color: string;
}

interface StatItem {
  value: string;
  label: string;
  icon: typeof Users;
}

const userTypeConfig: Record<
  UserType,
  {
    title: string;
    subtitle: string;
    description: string;
    gradient: string;
    accentColor: string;
    features: FeatureItem[];
    stats: StatItem[];
  }
> = {
  petso: {
    title: "Welcome to\nYour Pet's\nDigital Future",
    subtitle: 'Petso',
    description:
      "The world's first blockchain-powered platform for complete pet care management",
    gradient: 'linear-gradient(135deg, #2D1F7B 0%, #594BFF 50%, #8B7FFF 100%)',
    accentColor: '#594BFF',
    features: [
      {
        icon: Shield,
        title: 'Blockchain-Secured Records',
        description:
          "Your pet's health and identity data protected with military-grade encryption on Solana",
        color: '#594BFF',
      },
      {
        icon: Heart,
        title: 'Complete Health Management',
        description:
          'Track vaccinations, medications, and vet visits all in one secure digital space',
        color: '#FF6B9D',
      },
      {
        icon: Users,
        title: 'Connect with Professionals',
        description:
          'Access 7+ types of certified pet professionals from vets to trainers',
        color: '#10B981',
      },
      {
        icon: Award,
        title: 'Earn Rewards',
        description:
          'Get rewarded for keeping your pet healthy and engaged with the community',
        color: '#F59E0B',
      },
    ],
    stats: [
      { value: '50K+', label: 'Pet Parents', icon: Users },
      { value: '200K+', label: 'Pets Protected', icon: Heart },
      { value: '5K+', label: 'Professionals', icon: Award },
      { value: '99.9%', label: 'Uptime', icon: CheckCircle },
    ],
  },
  'petso-pro': {
    title: 'Grow Your\nPet Care\nBusiness',
    subtitle: 'Petso Pro',
    description:
      'Professional tools for veterinarians, trainers, groomers, and pet care specialists',
    gradient: 'linear-gradient(135deg, #1E1547 0%, #2D1F7B 50%, #594BFF 100%)',
    accentColor: '#2D1F7B',
    features: [
      {
        icon: Briefcase,
        title: 'Business Dashboard',
        description:
          'Manage appointments, clients, and revenue all in one professional interface',
        color: '#2D1F7B',
      },
      {
        icon: BarChart3,
        title: 'Analytics & Insights',
        description:
          'Track your business growth with real-time analytics and performance metrics',
        color: '#594BFF',
      },
      {
        icon: UserCheck,
        title: 'Client Management',
        description:
          'Build lasting relationships with integrated CRM and communication tools',
        color: '#10B981',
      },
      {
        icon: Award,
        title: 'Verified Credentials',
        description:
          'Showcase your certifications and build trust with verified badges',
        color: '#F59E0B',
      },
    ],
    stats: [
      { value: '5K+', label: 'Professionals', icon: Briefcase },
      { value: '50K+', label: 'Appointments', icon: Activity },
      { value: '4.9', label: 'Avg Rating', icon: Award },
      { value: '127', label: 'Countries', icon: Users },
    ],
  },
  farmso: {
    title: 'Modern\nLivestock\nManagement',
    subtitle: 'Farmso',
    description:
      'Digital identity and health tracking for livestock with government compliance',
    gradient: 'linear-gradient(135deg, #065F46 0%, #16A34A 50%, #22C55E 100%)',
    accentColor: '#16A34A',
    features: [
      {
        icon: Wheat,
        title: 'Livestock DID',
        description:
          'Unique digital identities for each animal with complete traceability',
        color: '#16A34A',
      },
      {
        icon: FileCheck,
        title: 'Compliance Ready',
        description:
          'Meet government regulations with automated reporting and documentation',
        color: '#065F46',
      },
      {
        icon: Truck,
        title: 'Supply Chain',
        description:
          'Track from farm to table with blockchain-verified supply chain records',
        color: '#22C55E',
      },
      {
        icon: BarChart3,
        title: 'Farm Analytics',
        description:
          'Optimize operations with data-driven insights and forecasting',
        color: '#10B981',
      },
    ],
    stats: [
      { value: '10K+', label: 'Farms', icon: Building2 },
      { value: '500K+', label: 'Animals', icon: Wheat },
      { value: '100%', label: 'Traceable', icon: FileCheck },
      { value: '24/7', label: 'Support', icon: CheckCircle },
    ],
  },
};

export function ClerkLoginPage({
  userType,
  onBack,
  redirectUrl,
}: ClerkLoginPageProps) {
  const [mode, setMode] = useState<'signIn' | 'signUp'>('signIn');
  const [activeFeature, setActiveFeature] = useState(0);
  const { colors, theme } = useTheme();
  const config = userTypeConfig[userType];

  // Only show left (marketing) panel on desktop — keeps it out of DOM on mobile so no sidebar
  const [isDesktop, setIsDesktop] = useState(false);
  useEffect(() => {
    const mql = window.matchMedia('(min-width: 1024px)');
    const update = () => setIsDesktop(mql.matches);
    update();
    mql.addEventListener('change', update);
    return () => mql.removeEventListener('change', update);
  }, []);

  const defaultRedirectUrl =
    userType === 'petso-pro'
      ? '/pro/dashboard'
      : `/${userType === 'petso' ? 'petso' : userType}/dashboard`;

  const clerkAppearance = {
    elements: {
      rootBox: 'w-full',
      card: 'shadow-none border-0 bg-transparent p-0',
      headerTitle: 'hidden',
      headerSubtitle: 'hidden',
      socialButtonsBlockButton: `border-2 rounded-xl py-3 hover:border-current transition-colors ${
        theme === 'dark'
          ? 'border-white/30 bg-[#141320]'
          : 'border-[#E2DFEA] bg-white'
      }`,
      socialButtonsBlockButtonText: `${theme === 'dark' ? 'text-white/90' : 'text-[#2D1F7B]'} font-medium`,
      dividerLine: theme === 'dark' ? 'bg-white/30' : 'bg-[#E2DFEA]',
      dividerText: `${theme === 'dark' ? 'text-white/75' : 'text-[#8A8796]'} px-2`,
      formButtonPrimary: 'rounded-xl py-3',
      formFieldInput: 'rounded-xl',
      footerAction: 'hidden',
    },
    variables: {
      colorPrimary: config.accentColor,
      borderRadius: '0.75rem',
      ...(theme === 'dark' && {
        colorBackground: '#0B0A12',
        colorInputBackground: '#16151D',
        colorInputText: '#ffffff',
        colorText: '#ffffff',
        colorTextSecondary: 'rgba(255,255,255,0.7)',
      }),
    },
  };

  // Auto-rotate features
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % config.features.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [config.features.length]);

  return (
    <div
      className="clerk-login-root min-h-screen flex flex-col lg:flex-row bg-[#F7F6FA] dark:bg-[#0B0A12]"
      style={{
        width: '100%',
        maxWidth: '100%',
        overflowX: 'hidden',
        boxSizing: 'border-box',
      }}
    >
      <style>{`
        @media (min-width: 1024px) {
          .clerk-login-root { flex-direction: row !important; }
          .clerk-login-left { flex: 0 0 50% !important; width: 50% !important; max-width: 50% !important; min-width: 0 !important; display: flex !important; }
          .clerk-login-right { flex: 0 0 50% !important; width: 50% !important; max-width: 50% !important; min-width: 0 !important; }
        }
      `}</style>
      {/* Theme toggle: fixed top-right, icon only — never in flow so no left strip */}
      <div
        style={{ position: 'fixed', top: 12, right: 12, zIndex: 9999 }}
        aria-hidden="false"
      >
        <ThemeToggle position="relative" iconOnly />
      </div>

      {/* Left Side - Welcome block (hidden on mobile, 50% on desktop) */}
      <div
        className="clerk-login-left hidden lg:flex lg:w-1/2 text-white p-12 flex-col justify-between relative overflow-hidden flex-shrink-0"
        style={{ background: config.gradient }}
      >
        {/* Animated Background Elements */}
        <div className="absolute inset-0 opacity-10">
          <div
            className="absolute top-20 left-20 w-64 h-64 rounded-full"
            style={{
              background:
                'radial-gradient(circle, rgba(255,255,255,0.3) 0%, transparent 70%)',
              animation: 'float 6s ease-in-out infinite',
            }}
          />
          <div
            className="absolute bottom-20 right-20 w-96 h-96 rounded-full"
            style={{
              background:
                'radial-gradient(circle, rgba(255,255,255,0.2) 0%, transparent 70%)',
              animation: 'float 8s ease-in-out infinite',
              animationDelay: '2s',
            }}
          />
        </div>

        <div className="relative z-10">
          {/* Logo & Header */}
          <div className="mb-12">
            <img
              src={petsoLogo}
              alt="Petso"
              className="h-10 mb-6"
              style={{
                objectFit: 'contain',
                filter: 'brightness(0) invert(1)',
              }}
            />
            <h1
              className="text-5xl mb-4 leading-tight whitespace-pre-line"
              style={{
                fontFamily: 'Sora, sans-serif',
                fontWeight: 700,
                letterSpacing: '-0.02em',
              }}
            >
              {config.title}
            </h1>
            <p
              className="text-xl text-gray-200"
              style={{ fontFamily: 'Inter, sans-serif', lineHeight: 1.6 }}
            >
              {config.description}
            </p>
          </div>

          {/* Animated Feature Cards */}
          <div className="space-y-4">
            {config.features.map((feature, index) => {
              const Icon = feature.icon;
              const isActive = activeFeature === index;
              return (
                <div
                  key={index}
                  className="p-5 backdrop-blur-sm transition-all duration-500 cursor-pointer"
                  style={{
                    backgroundColor: isActive
                      ? 'rgba(255, 255, 255, 0.15)'
                      : 'rgba(255, 255, 255, 0.08)',
                    borderRadius: '20px',
                    border: isActive
                      ? '2px solid rgba(255, 255, 255, 0.3)'
                      : '2px solid transparent',
                    transform: isActive ? 'scale(1.02)' : 'scale(1)',
                    boxShadow: isActive
                      ? '0 12px 32px rgba(0, 0, 0, 0.15)'
                      : 'none',
                  }}
                  onMouseEnter={() => setActiveFeature(index)}
                >
                  <div className="flex items-start gap-4">
                    <div
                      className="w-12 h-12 flex items-center justify-center flex-shrink-0"
                      style={{
                        backgroundColor: isActive
                          ? feature.color
                          : 'rgba(255, 255, 255, 0.2)',
                        borderRadius: '14px',
                        transition: 'all 0.3s ease',
                      }}
                    >
                      <Icon className="w-6 h-6" />
                    </div>
                    <div>
                      <h3
                        className="text-lg mb-1"
                        style={{
                          fontFamily: 'Sora, sans-serif',
                          fontWeight: 600,
                        }}
                      >
                        {feature.title}
                      </h3>
                      <p
                        className="text-sm text-gray-200"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        {feature.description}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Stats Section */}
        <div className="relative z-10">
          <div className="grid grid-cols-4 gap-4">
            {config.stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div key={index} className="text-center">
                  <Icon className="w-6 h-6 mx-auto mb-2 opacity-80" />
                  <div
                    className="text-2xl mb-1"
                    style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
                  >
                    {stat.value}
                  </div>
                  <div
                    className="text-xs text-gray-300"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {stat.label}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Right Side - Sign In / Sign Up form (desktop: right column; mobile: full width) */}
      <div
        className="clerk-login-right w-full min-w-0 flex-1 flex flex-col items-center justify-center px-4 py-6 sm:px-6 lg:p-8 lg:w-1/2 pt-14 lg:pt-8"
        style={{
          width: '100%',
          maxWidth: isDesktop ? undefined : '100%',
          boxSizing: 'border-box',
          overflowX: 'hidden',
        }}
      >
        <div
          className="w-full mx-auto flex flex-col items-center"
          style={{
            maxWidth: isDesktop ? '28rem' : 'min(100%, 320px)',
            boxSizing: 'border-box',
            marginLeft: 'auto',
            marginRight: 'auto',
          }}
        >
          {/* Back Button */}
          {onBack && (
            <button
              onClick={onBack}
              className="mb-6 flex items-center gap-2 hover:gap-3 transition-all"
              style={{
                color: colors.text,
                fontFamily: 'Sora, sans-serif',
                fontWeight: 600,
              }}
            >
              <ArrowLeft className="w-5 h-5" />
              Back to Home
            </button>
          )}

          {/* Mobile Header: compact on small screens (no subtitle text) */}
          <div className="lg:hidden mb-6 text-center">
            <img
              src={petsoLogo}
              alt="Petso"
              className="h-7 mx-auto mb-3"
              style={{ objectFit: 'contain' }}
            />
            <p
              className="text-xs max-w-[280px] mx-auto"
              style={{ fontFamily: 'Inter, sans-serif', color: colors.text }}
            >
              {config.description}
            </p>
          </div>

          {/* Auth Card */}
          <div
            className="login-auth-card p-5 sm:p-6 lg:p-8 rounded-2xl sm:rounded-[32px] border"
            style={{
              backgroundColor: colors.cardBg,
              borderColor: colors.border,
              boxShadow: `0 20px 60px ${config.accentColor}20`,
            }}
          >
            <div
              className="flex gap-1.5 p-1 rounded-xl mb-4 sm:mb-6"
              style={{ backgroundColor: colors.surfaceHover }}
            >
              <button
                onClick={() => setMode('signIn')}
                className="flex-1 py-2.5 sm:py-3 px-3 sm:px-4 rounded-lg text-xs sm:text-sm font-medium transition-all"
                style={{
                  fontFamily: 'Sora, sans-serif',
                  backgroundColor:
                    mode === 'signIn' ? colors.cardBg : 'transparent',
                  color: mode === 'signIn' ? config.accentColor : colors.text,
                  boxShadow:
                    mode === 'signIn' ? '0 2px 8px rgba(0,0,0,0.1)' : 'none',
                }}
              >
                Sign In
              </button>
              <button
                onClick={() => setMode('signUp')}
                className="flex-1 py-2.5 sm:py-3 px-3 sm:px-4 rounded-lg text-xs sm:text-sm font-medium transition-all"
                style={{
                  fontFamily: 'Sora, sans-serif',
                  backgroundColor:
                    mode === 'signUp' ? colors.cardBg : 'transparent',
                  color: mode === 'signUp' ? config.accentColor : colors.text,
                  boxShadow:
                    mode === 'signUp' ? '0 2px 8px rgba(0,0,0,0.1)' : 'none',
                }}
              >
                Sign Up
              </button>
            </div>

            <div className="clerk-container w-full min-w-0 max-w-full overflow-x-hidden">
              {mode === 'signIn' ? (
                <SignIn
                  routing="hash"
                  forceRedirectUrl={redirectUrl || defaultRedirectUrl}
                  appearance={clerkAppearance}
                />
              ) : (
                <SignUp
                  routing="hash"
                  forceRedirectUrl={redirectUrl || defaultRedirectUrl}
                  appearance={clerkAppearance}
                />
              )}
            </div>

            <label
              className="flex items-start gap-2 mt-4 cursor-pointer select-none"
              style={{
                fontFamily: 'Inter, sans-serif',
                color: colors.text,
                fontSize: '0.8125rem',
              }}
            >
              <input
                type="checkbox"
                defaultChecked
                className="mt-0.5 rounded border-gray-300 dark:border-gray-600"
                style={{ accentColor: config.accentColor }}
                aria-label="By creating an account, I agree to the Terms and Conditions and Privacy Policy"
              />
              <span>
                By creating an account, I agree to the{' '}
                <a
                  href="https://www.petso.io/documents/terms-of-service/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:opacity-90"
                  style={{ color: config.accentColor }}
                >
                  Terms and Conditions
                </a>{' '}
                and{' '}
                <a
                  href="https://www.petso.io/documents/privacy-policy/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:opacity-90"
                  style={{ color: config.accentColor }}
                >
                  Privacy Policy
                </a>
                .
              </span>
            </label>
          </div>

          <div
            className="mt-4 sm:mt-6 p-3 sm:p-4 rounded-xl text-center"
            style={{
              backgroundColor: `${config.accentColor}10`,
              border: `1px solid ${config.accentColor}30`,
            }}
          >
            <div className="flex items-center justify-center gap-2 mb-1">
              <Shield
                className="w-4 h-4"
                style={{ color: config.accentColor }}
              />
              <span
                className="text-sm"
                style={{
                  fontFamily: 'Sora, sans-serif',
                  fontWeight: 600,
                  color: colors.heading,
                }}
              >
                Blockchain-Secured Platform
              </span>
            </div>
            <p
              className="text-xs"
              style={{ fontFamily: 'Inter, sans-serif', color: colors.text }}
            >
              Your data is encrypted and protected with enterprise-grade
              security
            </p>
          </div>

          <div
            className="flex flex-wrap items-center justify-center gap-3 sm:gap-6 mt-3 sm:mt-4"
            style={{ color: colors.textSecondary }}
          >
            <div className="flex items-center gap-2">
              <div
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: '#10B981' }}
              />
              <span
                className="text-xs"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                SOC 2 Certified
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: '#10B981' }}
              />
              <span
                className="text-xs"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                GDPR Compliant
              </span>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) scale(1); }
          50% { transform: translateY(-20px) scale(1.05); }
        }

        .dark .clerk-container .cl-socialButtonsBlockButton {
          border-color: rgba(255, 255, 255, 0.35) !important;
          background-color: #141320 !important;
        }

        .dark .clerk-container .cl-socialButtonsBlockButtonText {
          color: rgba(255, 255, 255, 0.9) !important;
        }

        .dark .clerk-container .cl-dividerLine {
          background-color: rgba(255, 255, 255, 0.32) !important;
        }

        .dark .clerk-container .cl-dividerText {
          color: rgba(255, 255, 255, 0.75) !important;
        }

        .dark .clerk-container .cl-formFieldInput {
          background-color: #16151D !important;
          color: #ffffff !important;
          border-color: rgba(255, 255, 255, 0.25) !important;
        }

        .dark .clerk-container .cl-formFieldInput::placeholder {
          color: rgba(255, 255, 255, 0.58) !important;
        }

        .dark .clerk-container .cl-formFieldInput:-webkit-autofill,
        .dark .clerk-container .cl-formFieldInput:-webkit-autofill:hover,
        .dark .clerk-container .cl-formFieldInput:-webkit-autofill:focus {
          -webkit-text-fill-color: #ffffff !important;
          box-shadow: 0 0 0 1000px #16151D inset !important;
          -webkit-box-shadow: 0 0 0 1000px #16151D inset !important;
          caret-color: #ffffff !important;
          border-color: rgba(255, 255, 255, 0.25) !important;
          transition: background-color 9999s ease-out 0s;
        }
      `}</style>
    </div>
  );
}
